import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, Upload, X, ChevronUp, ChevronDown, Save, FileText, ExternalLink } from "lucide-react";
import { fetchProjects, createProject, updateProject, deleteProject, uploadFile, fileUrl } from "../../lib/api";
import VideosEditor from "./VideosEditor";

const inputClass = "bg-[#F6F1E8] border border-[#1A1714]/15 rounded-sm focus-visible:ring-1 focus-visible:ring-[#1A1714]";

const emptyProject = () => ({
  title: "New project",
  client: "",
  year: "",
  tags: [],
  summary: "",
  metrics: [],
  cover: "",
  gallery: [],
  documents: [],
  videos: [],
  docs_link: "",
});

export default function AdminProjects() {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // project being edited (null = list view)
  const [busy, setBusy] = useState(false);

  const reload = async () => setProjects(await fetchProjects());
  useEffect(() => { reload(); }, []);

  const startNew = async () => {
    setBusy(true);
    try {
      const created = await createProject(emptyProject());
      await reload();
      setEditing(created);
    } catch (e) {
      toast({ title: "Could not create project", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await deleteProject(p.id);
    await reload();
    toast({ title: "Project deleted" });
  };

  if (editing) {
    return <ProjectEditor project={editing} onClose={async () => { setEditing(null); await reload(); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-[#1A1714]/65 text-sm">Add as many projects as you like. Each can have a cover, gallery images, downloadable documents and a Google Docs link.</p>
        <button onClick={startNew} disabled={busy} className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-4 py-2 rounded-sm text-[12px] tracking-[0.18em] uppercase hover:bg-[#2c2620] disabled:opacity-60">
          <Plus size={14} /> New project
        </button>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm overflow-hidden flex">
            <div className="w-32 h-32 bg-[#1A1714]/8 shrink-0">
              {p.cover && <img src={fileUrl(p.cover)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4 flex-1 flex flex-col justify-between">
              <div>
                <div className="font-serif-display text-lg leading-tight">{p.title || "Untitled"}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#1A1714]/55 mt-1">
                  {[p.client, p.year].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(p)} className="text-[11px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-1.5 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8]">Edit</button>
                <button onClick={() => remove(p)} className="text-[11px] tracking-[0.18em] uppercase text-[#B5523A] hover:underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="text-[#1A1714]/55 text-sm">No projects yet — click “New project” to start.</div>
        )}
      </div>
    </div>
  );
}

function ProjectEditor({ project, onClose }) {
  const { toast } = useToast();
  const [p, setP] = useState({ ...project, tags: project.tags || [], metrics: project.metrics || [], gallery: project.gallery || [], documents: project.documents || [], videos: project.videos || [] });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(""); // which upload is busy

  const set = (patch) => setP((cur) => ({ ...cur, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await updateProject(p.id, p);
      toast({ title: "Project saved" });
      onClose();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    set({ tags: [...(p.tags || []), t] });
    setTagInput("");
  };

  const handleUpload = async (e, target) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(target);
    try {
      const res = await uploadFile(f);
      if (target === "cover") set({ cover: res.url });
      else if (target === "gallery") set({ gallery: [...(p.gallery || []), res.url] });
      else if (target === "docs") set({ documents: [...(p.documents || []), { url: res.url, filename: res.filename }] });
      toast({ title: "Uploaded", description: res.filename });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/60 hover:text-[#1A1714]">← All projects</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-4 py-2 rounded-sm text-[12px] tracking-[0.18em] uppercase hover:bg-[#2c2620] disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save project"}
        </button>
      </div>

      <div className="mt-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Title</label>
            <Input className={inputClass} value={p.title} onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Year</label>
            <Input className={inputClass} value={p.year} onChange={(e) => set({ year: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Client / context</label>
          <Input className={inputClass} value={p.client} onChange={(e) => set({ client: e.target.value })} placeholder="e.g. Internship · Social Campaign" />
        </div>

        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Summary</label>
          <Textarea rows={4} className={inputClass} value={p.summary} onChange={(e) => set({ summary: e.target.value })} />
        </div>

        {/* Tags */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Tags</label>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {(p.tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-[#1A1714] text-[#F6F1E8] text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full">
                {t}
                <button type="button" onClick={() => set({ tags: p.tags.filter((_, k) => k !== i) })}><X size={12} /></button>
              </span>
            ))}
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Add tag and press Enter" className="flex-1 min-w-[180px] bg-transparent border-b border-[#1A1714]/20 focus:outline-none focus:border-[#1A1714] py-1.5 text-sm" />
          </div>
        </div>

        {/* Metrics */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Metrics (up to 3)</label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(p.metrics || []).map((m, i) => (
              <div key={i} className="bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm p-3 relative">
                <button type="button" onClick={() => set({ metrics: p.metrics.filter((_, k) => k !== i) })} className="absolute top-2 right-2 text-[#B5523A]"><Trash2 size={14} /></button>
                <Input className={inputClass} placeholder="Value (e.g. 3×)" value={m.value} onChange={(e) => set({ metrics: p.metrics.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)) })} />
                <Input className={`${inputClass} mt-2`} placeholder="Label (e.g. Organic Traffic)" value={m.label} onChange={(e) => set({ metrics: p.metrics.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)) })} />
              </div>
            ))}
            {(p.metrics || []).length < 3 && (
              <button type="button" onClick={() => set({ metrics: [...(p.metrics || []), { value: "", label: "" }] })} className="border border-dashed border-[#1A1714]/30 rounded-sm p-3 text-sm text-[#1A1714]/60 hover:bg-[#1A1714]/5">+ Add metric</button>
            )}
          </div>
        </div>

        {/* Cover */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Cover image</label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-40 aspect-[4/3] bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm overflow-hidden">
              {p.cover && <img src={fileUrl(p.cover)} alt="" className="w-full h-full object-cover" />}
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer text-[12px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors">
              <Upload size={14} /> {busy === "cover" ? "Uploading…" : "Upload cover"}
              <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "cover")} className="hidden" />
            </label>
            {p.cover && (<button type="button" onClick={() => set({ cover: "" })} className="text-[12px] text-[#B5523A] hover:underline">Remove</button>)}
          </div>
        </div>

        {/* Gallery */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Gallery images</label>
          <div className="mt-2 flex flex-wrap gap-3">
            {(p.gallery || []).map((g, i) => (
              <div key={i} className="relative w-28 aspect-square bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm overflow-hidden">
                <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => set({ gallery: p.gallery.filter((_, k) => k !== i) })} className="absolute top-1 right-1 bg-[#1A1714]/80 text-[#F6F1E8] rounded-full p-1"><X size={12} /></button>
              </div>
            ))}
            <label className="w-28 aspect-square border border-dashed border-[#1A1714]/30 rounded-sm flex items-center justify-center cursor-pointer text-xs text-[#1A1714]/60 hover:bg-[#1A1714]/5">
              {busy === "gallery" ? "…" : <><Plus size={16} /></>}
              <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "gallery")} className="hidden" />
            </label>
          </div>
        </div>

        {/* Videos */}
        <VideosEditor
          videos={p.videos || []}
          onChange={(videos) => set({ videos })}
          label="Videos"
          helper="Upload .mp4 / .mov / .webm (up to 500 MB) or paste YouTube / Vimeo / TikTok links. Visitors click play to watch."
        />

        {/* Documents */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Downloadable documents (PDF / DOC / DOCX)</label>
          <div className="mt-2 space-y-2">
            {(p.documents || []).map((d, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm px-3 py-2">
                <FileText size={14} className="text-[#B5523A]" />
                <a href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm hover:underline">{d.filename}</a>
                <button type="button" onClick={() => set({ documents: p.documents.filter((_, k) => k !== i) })} className="text-[#B5523A]"><Trash2 size={14} /></button>
              </div>
            ))}
            <label className="inline-flex items-center gap-2 cursor-pointer text-[12px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors">
              <Upload size={14} /> {busy === "docs" ? "Uploading…" : "Upload document"}
              <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleUpload(e, "docs")} className="hidden" />
            </label>
          </div>
        </div>

        {/* Google Docs link */}
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55">Google Docs share link (optional)</label>
          <Input className={inputClass} placeholder="https://docs.google.com/document/d/…" value={p.docs_link} onChange={(e) => set({ docs_link: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
